import { parseLengthInput } from "./lengthParse";
import { getShowSecondaryUnits, getUnits } from "../preferences";
import { wireLengthFieldEnter } from "./lengthFieldEnter";

/**
 * The app's standard unit-aware length input — two synced number fields
 * labelled "mm" and "in" sharing one parser, units preference, and
 * validation. Lives in its own module (rather than alongside the big
 * PropertiesPanel) so lightweight callers — the advanced-print dialog, the
 * trace-an-image calibration step — can use it without pulling PropertiesPanel's
 * heavy paper.js dependency. PropertiesPanel re-exports it for back-compat.
 */

const MM_PER_INCH = 25.4;

function formatMm(mm: number): string {
  return Number.isFinite(mm) ? mm.toFixed(1) : "";
}
function formatIn(mm: number): string {
  return Number.isFinite(mm) ? (mm / MM_PER_INCH).toFixed(3) : "";
}

export interface MmInchOptions {
  minMm?: number;
  maxMm?: number;
  mmStep?: number;
  inStep?: number;
  /** When true the value is allowed to be negative (e.g. offset distance). */
  allowNegative?: boolean;
  /** When true, `onChange` fires on every keystroke (`input` event) in
   *  addition to blur / Enter (`change`). Use sparingly — each fired
   *  change typically pushes a history entry, so a callsite that
   *  enables this is opting into "one undo per character". Default
   *  off to preserve the historical "commit on blur" UX. */
  liveCommit?: boolean;
  /**
   * When true, `onChange` fires after a short pause in typing, as well as on
   * blur / Enter.
   *
   * The middle ground between the default and {@link liveCommit}: the value
   * takes effect while you type without one history entry per character. It
   * exists because commit-on-blur is a keyboard assumption (#40) — on a tablet
   * the floating keyboard covers the canvas, so "type, then dismiss the
   * keyboard to see what happened" is the whole interaction, and if the
   * platform does not deliver the `change` the field is waiting for, nothing
   * happens at all.
   */
  commitWhileTyping?: boolean;
}

/**
 * Two synced number inputs labelled "mm" and "in". `onChange` is always
 * invoked with the value in millimetres regardless of which field the user
 * edited. Returns a `setValue` so external controls (sliders, preset chips,
 * data-binding refreshes) can drive both inputs without manual DOM lookups.
 */
export function mmInchInputs(
  initialMm: number,
  onChange: (mm: number) => void,
  opts: MmInchOptions = {},
): { row: HTMLElement; setValue: (mm: number) => void } {
  const allowNegative = opts.allowNegative ?? false;
  const valid = (v: number) => {
    if (!Number.isFinite(v)) return false;
    if (allowNegative) return true;
    // When a minMm is given, anchor positivity to that bound (lets callers
    // accept exactly 0, e.g. "Spacing"). Otherwise default to strictly > 0.
    return opts.minMm !== undefined ? v >= opts.minMm : v > 0;
  };

  const row = document.createElement("div");
  row.className = "mm-in-row";

  // type="text" + inputmode="decimal" so users can type fractions
  // like `1/8` or mixed forms like `1 1/2` — `type="number"` would
  // silently reject the slash and force `parseFloat` truncation.
  // The mobile numeric keyboard still surfaces via `inputmode`.
  const mm = document.createElement("input");
  mm.type = "text";
  mm.inputMode = "decimal";
  mm.autocomplete = "off";
  mm.spellcheck = false;
  mm.value = formatMm(initialMm);

  const inch = document.createElement("input");
  inch.type = "text";
  inch.inputMode = "decimal";
  inch.autocomplete = "off";
  inch.spellcheck = false;
  inch.value = formatIn(initialMm);

  let lastValid = initialMm;

  // Debounce for `commitWhileTyping`. Matches the preset dimension fields'
  // idle window, so the two kinds of dimension field feel the same.
  const TYPING_IDLE_MS = 400;
  let pendingTimer: ReturnType<typeof setTimeout> | null = null;
  const cancelPending = () => {
    if (pendingTimer !== null) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
  };
  const scheduleCommit = (mmValue: number) => {
    if (!opts.commitWhileTyping) return;
    cancelPending();
    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      // The panel re-renders freely; a timer that outlived its input must not
      // resurrect a stale value.
      if (!mm.isConnected && !inch.isConnected) return;
      lastValid = mmValue;
      onChange(mmValue);
    }, TYPING_IDLE_MS);
  };

  const commitMm = () => {
    cancelPending();
    const parsed = parseLengthInput(mm.value, "mm");
    if (parsed !== null && valid(parsed)) {
      lastValid = parsed;
      mm.value = formatMm(parsed);
      inch.value = formatIn(parsed);
      onChange(parsed);
    } else {
      mm.value = formatMm(lastValid);
    }
  };
  const commitIn = () => {
    cancelPending();
    const parsed = parseLengthInput(inch.value, "in");
    if (parsed !== null && Number.isFinite(parsed)) {
      const mmV = parsed * MM_PER_INCH;
      if (valid(mmV)) {
        lastValid = mmV;
        inch.value = formatIn(mmV);
        mm.value = formatMm(mmV);
        onChange(mmV);
        return;
      }
    }
    inch.value = formatIn(lastValid);
  };
  mm.addEventListener("change", commitMm);
  // Enter leaves the field — or, for a field focused by touch, moves on to
  // the next length field so the on-screen keyboard stays up (#1151).
  wireLengthFieldEnter(mm);
  inch.addEventListener("change", commitIn);
  wireLengthFieldEnter(inch);
  // Live cross-unit display sync. `input` fires on every keystroke (paste,
  // backspace, etc.) so the paired field reflects the typed value
  // immediately. By default we only update the paired *display*; the
  // actual commit (writing to history / settings / tool state via
  // onChange) waits for `change` on blur or Enter so quick mid-edit
  // values don't pollute the undo stack with one entry per character.
  // When the caller passes `liveCommit: true` we also fire `onChange`
  // here so the model tracks the typed value in real time.
  mm.addEventListener("input", () => {
    const parsed = parseLengthInput(mm.value, "mm");
    if (parsed !== null && valid(parsed)) {
      inch.value = formatIn(parsed);
      if (opts.liveCommit) {
        lastValid = parsed;
        onChange(parsed);
      } else {
        scheduleCommit(parsed);
      }
    } else if (mm.value.trim() === "") {
      inch.value = "";
      cancelPending();
    }
  });
  inch.addEventListener("input", () => {
    const parsed = parseLengthInput(inch.value, "in");
    if (parsed !== null) {
      const mmV = parsed * MM_PER_INCH;
      if (valid(mmV)) {
        mm.value = formatMm(mmV);
        if (opts.liveCommit) {
          lastValid = mmV;
          onChange(mmV);
        } else {
          scheduleCommit(mmV);
        }
      }
    } else if (inch.value.trim() === "") {
      mm.value = "";
    }
  });

  // Order the paired inputs so the user's preferred unit appears first;
  // both inputs stay visible so values can still be typed in either unit.
  // When the user has hidden secondary units (Settings → "Show secondary
  // units"), only the primary cell is mounted. The hidden input is still
  // wired up so the cross-unit value sync in `commitMm` / `commitIn` keeps
  // working internally — flipping the preference back on mid-session
  // surfaces an already-up-to-date value.
  const cells: HTMLElement[] = [
    makeUnitCell(mm, "mm"),
    makeUnitCell(inch, "in"),
  ];
  if (getUnits() === "in") cells.reverse();
  const visibleCells = getShowSecondaryUnits() ? cells : [cells[0]!];
  for (const cell of visibleCells) row.appendChild(cell);

  return {
    row,
    setValue: (mmV: number) => {
      lastValid = mmV;
      mm.value = formatMm(mmV);
      inch.value = formatIn(mmV);
    },
  };
}

export function makeUnitCell(
  input: HTMLInputElement,
  unit: string,
): HTMLElement {
  const cell = document.createElement("div");
  cell.className = "mm-in-cell";
  cell.appendChild(input);
  const u = document.createElement("span");
  u.className = "mm-in-unit";
  u.textContent = unit;
  cell.appendChild(u);
  return cell;
}
